import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface ReferenceOption {
  /** Код значения в справочнике: subjectID предмета WB, ID категории OZON. */
  value: string;
  /** Человеческое название — по нему идёт поиск. */
  label: string;
  /** Строка-пояснение под названием: родитель категории, число типов и т. п. */
  hint?: string;
}

interface ReferenceComboboxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: ReferenceOption[];
  /**
   * Подпись выбранного значения, когда его нет в текущем `options`. Нужна при
   * серверном поиске: список подменяется результатами запроса, а показать
   * выбранное всё равно надо.
   */
  selectedLabel?: string;
  /** Поиск на стороне сервера: справочник больше, чем стоит держать в браузере. */
  onSearch?: (query: string) => void;
  loading?: boolean;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

type PanelPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
};

/**
 * Сколько строк рисуем разом. Справочники площадок — тысячи позиций (7 193
 * предмета WB), и рисовать их целиком незачем: человек всё равно ищет вводом,
 * а тысячи узлов в DOM подвешивают открытие списка.
 */
const VISIBLE_LIMIT = 60;

/** Задержка серверного поиска: набор текста не должен слать запрос на букву. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * ReferenceCombobox — выбор ОДНОГО значения справочника по коду и названию.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ КОМПОНЕНТ. `Combobox` работает со списком строк: значение и
 * подпись у него одно и то же. Справочники маркетплейсов так не устроены —
 * отправляется код (3421), а человек ищет по названию («Декор для выпечки»).
 * Класть код в текст («3421 — Декор для выпечки») и разбирать его обратно —
 * это парсинг подписи, который ломается на первом же названии с тире.
 *
 * Код показывается моноширинным: это артикул площадки, а не текст.
 */
export function ReferenceCombobox({
  value,
  onChange,
  options,
  selectedLabel,
  onSearch,
  loading = false,
  placeholder = 'Выберите или начните вводить',
  emptyText = 'Ничего не найдено',
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
}: ReferenceComboboxProps) {
  const reactId = useId();
  const inputId = id ?? `reference-combobox-${reactId}`;
  const listboxId = `${inputId}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [position, setPosition] = useState<PanelPosition | null>(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const displayLabel = selected?.label ?? selectedLabel ?? (value ? value : '');

  // Локальная фильтрация остаётся и при серверном поиске: ответ приходит с
  // задержкой, и без неё список на мгновение показывает чужие строки.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) => option.label.toLowerCase().includes(q) || option.value.includes(q),
    );
  }, [options, query]);
  const visible = filtered.slice(0, VISIBLE_LIMIT);
  const hiddenCount = filtered.length - visible.length;

  useEffect(() => {
    if (!onSearch) return;
    const handle = window.setTimeout(() => onSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const updatePosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 240), viewportWidth - 16);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, viewportWidth - width - 8));
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const placeAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
    const available = Math.max(140, placeAbove ? spaceAbove - 4 : spaceBelow - 4);
    setPosition({
      left,
      width,
      maxHeight: Math.min(320, available),
      ...(placeAbove ? { bottom: viewportHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    setHighlighted(0);
    setOpen(true);
  };

  const choose = (option: ReferenceOption) => {
    onChange(option.value);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openMenu();
      else setHighlighted((h) => Math.min(h + 1, visible.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && visible[highlighted]) choose(visible[highlighted]);
      else setOpen(false);
    } else if (event.key === 'Escape') {
      // Закрытый список Esc не трогает: его получит окно вокруг. Открытый — закрывается и гасит событие.
      if (!open) return;
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const panel =
    open && position
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            className={cn(
              'fixed z-[80] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg',
              'dark:border-zinc-800 dark:bg-zinc-900',
            )}
            style={{
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            {loading && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 dark:text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Загрузка справочника
              </div>
            )}
            {!loading && visible.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400 dark:text-zinc-500">{emptyText}</div>
            )}
            {visible.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlighted;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlighted(index)}
                  onClick={() => choose(option)}
                  className={cn(
                    'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                    isSelected
                      ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300'
                      : 'text-gray-700 dark:text-zinc-200',
                    isHighlighted && !isSelected && 'bg-gray-50 dark:bg-zinc-800/70',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block whitespace-normal break-words">{option.label}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-400 dark:text-zinc-500">
                      <span className="font-mono">{option.value}</span>
                      {option.hint && <span>{option.hint}</span>}
                    </span>
                  </span>
                  {isSelected && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                </button>
              );
            })}
            {hiddenCount > 0 && (
              <div className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-400 dark:border-zinc-800 dark:text-zinc-500">
                Показаны первые {VISIBLE_LIMIT}, ещё {hiddenCount} — уточните запрос
              </div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        value={open ? query : displayLabel}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(0);
          if (!open) setOpen(true);
        }}
        onFocus={openMenu}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-14 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors',
          'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500',
          'disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400',
          'dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder-zinc-500 dark:disabled:bg-zinc-800',
        )}
      />
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
        {value && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
              onChange(null);
              setQuery('');
            }}
            className="pointer-events-auto rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-zinc-200"
            aria-label="Очистить"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-zinc-500',
            open && 'rotate-180',
          )}
        />
      </div>
      {panel}
    </div>
  );
}

export default ReferenceCombobox;
